function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
let MCPMarketplaceAPI = class MCPMarketplaceAPI {
    async fetchFromMultipleSources(endpoint, params) {
        const results = [];
        // 여러 마켓플레이스에서 병렬로 데이터 가져오기
        const promises = this.baseUrls.map(async (baseUrl)=>{
            try {
                const url = new URL(`${baseUrl}${endpoint}`);
                if (params) {
                    Object.keys(params).forEach((key)=>{
                        if (params[key] !== undefined) {
                            url.searchParams.append(key, params[key]);
                        }
                    });
                }
                const response = await fetch(url.toString(), {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                });
                if (!response.ok) {
                    console.warn(`Failed to fetch from ${baseUrl}: ${response.status}`);
                    return [];
                }
                const data = await response.json();
                return data.items || [];
            } catch (error) {
                console.warn(`Error fetching from ${baseUrl}:`, error);
                return [];
            }
        });
        const allResults = await Promise.allSettled(promises);
        // 성공한 결과만 수집
        allResults.forEach((result)=>{
            if (result.status === 'fulfilled') {
                results.push(...result.value);
            }
        });
        return this.deduplicateServers(results);
    }
    deduplicateServers(servers) {
        const seen = new Set();
        return servers.filter((server)=>{
            const key = `${server.content_name}-${server.publisher_id}`;
            if (seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });
    }
    normalizeServer(item) {
        // 서버 정보 정규화
        return {
            ...item,
            rating: item.rating || '0',
            review_cnt: item.review_cnt || '0',
            install_count: parseInt(item.review_cnt) * 10 || 0,
            transport_type: this.detectTransportType(item),
            auth_type: this.detectAuthType(item),
            capabilities: this.extractCapabilities(item),
            tags: this.generateTags(item),
            category: this.categorizeServer(item),
            version: '1.0.0',
            endpoint: this.generateEndpoint(item)
        };
    }
    detectTransportType(item) {
        const description = item.description?.toLowerCase() || '';
        const name = item.content_name?.toLowerCase() || '';
        if (description.includes('sse') || description.includes('server-sent events')) {
            return 'sse';
        }
        if (description.includes('websocket') || description.includes('ws://')) {
            return 'websocket';
        }
        return 'stdio'; // 기본값
    }
    detectAuthType(item) {
        const description = item.description?.toLowerCase() || '';
        if (description.includes('oauth') || description.includes('oauth2')) {
            return 'oauth2';
        }
        if (description.includes('api key') || description.includes('token')) {
            return 'api_key';
        }
        return 'none';
    }
    extractCapabilities(item) {
        const capabilities = [];
        const description = item.description?.toLowerCase() || '';
        const name = item.content_name?.toLowerCase() || '';
        // 도구 기반 capability 추출
        item.tools?.forEach((tool)=>{
            if (tool.name) {
                capabilities.push(tool.name.toLowerCase().replace(/[^a-z0-9]/g, '-'));
            }
        });
        // 설명에서 키워드 추출
        const keywords = [
            'github',
            'database',
            'api',
            'web',
            'file',
            'search',
            'data',
            'ai',
            'ml'
        ];
        keywords.forEach((keyword)=>{
            if (description.includes(keyword) || name.includes(keyword)) {
                capabilities.push(keyword);
            }
        });
        return [
            ...new Set(capabilities)
        ]; // duplicate removed
    }
    generateTags(item) {
        const tags = [];
        const description = item.description?.toLowerCase() || '';
        // 일반적인 태그 생성
        if (description.includes('database')) tags.push('database');
        if (description.includes('api')) tags.push('api');
        if (description.includes('web')) tags.push('web');
        if (description.includes('file')) tags.push('file-system');
        if (description.includes('search')) tags.push('search');
        if (description.includes('ai') || description.includes('ml')) tags.push('ai-ml');
        return tags;
    }
    categorizeServer(item) {
        const description = item.description?.toLowerCase() || '';
        const name = item.content_name?.toLowerCase() || '';
        if (description.includes('github') || description.includes('git')) return 'developer-tools';
        if (description.includes('database') || description.includes('sql')) return 'database';
        if (description.includes('api') || description.includes('webhook')) return 'api-integration';
        if (description.includes('web') || description.includes('scraping')) return 'web-scraping';
        if (description.includes('file') || description.includes('storage')) return 'file-system';
        if (description.includes('ai') || description.includes('ml')) return 'data-science';
        return 'productivity'; // 기본 카테고리
    }
    generateEndpoint(item) {
        // 실제 엔드포인트 생성 (예시)
        if (item.website && item.website.includes('github.com')) {
            // GitHub 레포지토리에서 SSE 엔드포인트 추정
            const repoPath = item.website.replace('https://github.com/', '');
            return `https://${repoPath.replace('/', '-')}.herokuapp.com/sse`;
        }
        // 기본 SSE 엔드포인트 패턴
        const serverName = item.content_name.toLowerCase().replace(/[^a-z0-9]/g, '-');
        return `https://${serverName}.mcp-server.com/sse`;
    }
    async searchServers(params = {}) {
        console.log('🔍 MCP Marketplace API: Searching servers...', params);
        try {
            // 여러 소스에서 데이터 가져오기
            const servers = await this.fetchFromMultipleSources('/servers', {
                q: params.query,
                category: params.category,
                page: params.page || 1,
                limit: params.limit || 50
            });
            // 서버 정보 정규화
            const normalizedServers = servers.map((server)=>this.normalizeServer(server));
            // 로컬 필터링
            let filteredServers = normalizedServers;
            if (params.transport) {
                filteredServers = filteredServers.filter((s)=>s.transport_type === params.transport);
            }
            if (params.auth_type) {
                filteredServers = filteredServers.filter((s)=>s.auth_type === params.auth_type);
            }
            // 정렬
            filteredServers.sort((a, b)=>{
                switch(params.sort_by){
                    case 'rating':
                        return parseFloat(b.rating) - parseFloat(a.rating);
                    case 'installs':
                        return (b.install_count || 0) - (a.install_count || 0);
                    case 'name':
                        return a.content_name.localeCompare(b.content_name);
                    default:
                        return parseFloat(b.rating) - parseFloat(a.rating);
                }
            });
            console.log(`✅ Found ${filteredServers.length} MCP servers`);
            return filteredServers;
        } catch (error) {
            console.error('❌ MCP Marketplace API Error:', error);
            // Fallback: 로컬 mock 데이터
            return this.getMockServers();
        }
    }
    async getServerDetails(serverId) {
        try {
            const servers = await this.fetchFromMultipleSources(`/servers/${serverId}`);
            return servers.length > 0 ? this.normalizeServer(servers[0]) : null;
        } catch (error) {
            console.error('Failed to get server details:', error);
            return null;
        }
    }
    async getCategories() {
        try {
            const servers = await this.searchServers();
            const categoryCounts = {};
            servers.forEach((server)=>{
                const category = server.category || 'other';
                categoryCounts[category] = (categoryCounts[category] || 0) + 1;
            });
            return Object.entries(categoryCounts).map(([id, count])=>({
                    id,
                    name: this.getCategoryDisplayName(id),
                    count
                }));
        } catch (error) {
            console.error('Failed to get categories:', error);
            return [];
        }
    }
    getCategoryDisplayName(id) {
        const names = {
            'developer-tools': '개발 도구',
            'database': '데이터베이스',
            'api-integration': 'API 통합',
            'web-scraping': '웹 스크래핑',
            'file-system': '파일 시스템',
            'data-science': '데이터 사이언스',
            'productivity': '생산성'
        };
        return names[id] || id;
    }
    getMockServers() {
        return [
            {
                content_name: 'GitHub Integration MCP',
                publisher_id: 'pub-github',
                website: 'https://github.com/github/mcp-server',
                review_cnt: '156',
                rating: '4.8',
                description: 'Comprehensive GitHub API integration for MCP with OAuth2 support',
                tools: [
                    {
                        name: 'list_repositories',
                        description: 'List user repositories'
                    },
                    {
                        name: 'create_issue',
                        description: 'Create new issue'
                    },
                    {
                        name: 'get_pull_requests',
                        description: 'Get pull requests'
                    }
                ],
                field: 'MCP SERVER',
                config: [],
                endpoint: 'https://github-mcp.herokuapp.com/sse',
                transport_type: 'sse',
                auth_type: 'oauth2',
                capabilities: [
                    'github',
                    'git',
                    'repositories',
                    'issues'
                ],
                tags: [
                    'developer-tools',
                    'git',
                    'oauth2'
                ],
                category: 'developer-tools',
                version: '2.1.0',
                install_count: 1247
            }
        ];
    }
    constructor(){
        _define_property(this, "baseUrls", [
            'https://www.deepnlp.org/api/mcp',
            'https://mcpmarket.com/api',
            'https://mcp.so/api',
            'https://pulsemcp.com/api'
        ]);
    }
};
export const mcpMarketplaceAPI = new MCPMarketplaceAPI();
